// src/stores/propsStore.ts
import { create } from 'zustand';
import { BasePick, Side, Sport, uid } from '../domain/types';
import { useAuthStore } from './authStore';

interface PropsState {
  // props unificadas NBA + NHL
  allProps: BasePick[];
  nbaProps: BasePick[];
  nhlProps: BasePick[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  // ações
  setProps: (props: BasePick[]) => void;
  setNbaProps: (props: BasePick[]) => void;
  setNhlProps: (props: BasePick[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // fetch do backend
  fetchAll: () => Promise<void>;
  fetchBySport: (sport: Sport) => Promise<void>;

  // filtros
  getPropsByGame: (game: string) => BasePick[];
  getBestByGame: (game: string, limit?: number) => BasePick[];
  getAllGames: () => string[];
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/+$/, '');
const PROPS_CACHE_TTL = 15 * 60 * 1000;
const PROPS_CACHE_PREFIX = 'rushprops.props.v1.';
let loadedCacheKey: string | null = null;
let inFlightKey: string | null = null;
let inFlight: Promise<void> | null = null;

function cacheKey(): string {
  const profile = useAuthStore.getState().profile;
  const identity = profile ? `${profile.email.toLowerCase()}|${profile.plan}|${profile.isAdmin ? 'admin' : 'member'}` : 'anonymous';
  return `${PROPS_CACHE_PREFIX}${identity}`;
}
function readCached(key: string): { nba: BasePick[]; nhl: BasePick[]; fetchedAt: number } | null {
  try {
    const value = JSON.parse(localStorage.getItem(key) || 'null');
    if (!value || Date.now() - value.fetchedAt > PROPS_CACHE_TTL || !Array.isArray(value.nba) || !Array.isArray(value.nhl)) return null;
    return value;
  } catch { return null; }
}

function authHeaders(): HeadersInit {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

type ApiHitSide = { hits?: number; total?: number; pct?: number };
type ApiProp = Partial<BasePick> & {
  player?: string;
  market?: string;
  label?: string;
  stat?: string;
  statKey?: string;
  line?: number;
  game?: string;
  start?: string;
  startISO?: string;
  price?: number | null;
  values?: number[];
  dates?: string[];
  hitRate?: { over?: ApiHitSide; under?: ApiHitSide };
  bestSide?: string;
  bestPct?: number;
  pct?: number;
};

function decimalOdds(prop: ApiProp): number {
  if (typeof prop.odds === 'number' && Number.isFinite(prop.odds) && prop.odds > 1) return prop.odds;
  const price = prop.price;
  if (typeof price !== 'number' || !Number.isFinite(price) || price === 0) return 1.91;
  return price > 0 ? 1 + price / 100 : 1 + 100 / Math.abs(price);
}

function normalizeProps(payload: unknown, sport: Sport): BasePick[] {
  const rows: ApiProp[] = Array.isArray(payload)
    ? payload as ApiProp[]
    : ((payload as { props?: ApiProp[] } | null)?.props ?? []);

  return rows.flatMap((prop) => {
    if (!prop.player || typeof prop.line !== 'number' || !prop.game) return [];
    const values = prop.values ?? [];
    const over = prop.hitRate?.over?.pct;
    const under = prop.hitRate?.under?.pct;
    const side: Side = prop.side === Side.UNDER || prop.bestSide === Side.UNDER
      ? Side.UNDER
      : prop.side === Side.OVER || prop.bestSide === Side.OVER
        ? Side.OVER
        : (over ?? -1) >= (under ?? -1) ? Side.OVER : Side.UNDER;
    const recent = values.slice(0, 20);
    const overValues = recent.filter(value => value !== prop.line);
    const overHits = overValues.filter(value => value > prop.line!).length;
    const underHits = overValues.filter(value => value < prop.line!).length;
    const calculatedOver = overValues.length ? Math.round(overHits * 100 / overValues.length) : undefined;
    const calculatedUnder = overValues.length ? Math.round(underHits * 100 / overValues.length) : undefined;
    const calculatedPct = side === Side.OVER ? calculatedOver : calculatedUnder;
    const hitRatePct = prop.hitRatePct ?? prop.bestPct ?? prop.pct ?? (side === 'over' ? over : under) ?? calculatedPct;
    const startISO = prop.startISO ?? prop.start ?? '';
    const market = prop.label ?? prop.market ?? 'Prop';

    return [{
      id: prop.id ?? uid(),
      sport,
      player: prop.player,
      market,
      statKey: prop.statKey ?? prop.stat ?? prop.market ?? market,
      line: prop.line,
      side,
      odds: decimalOdds(prop),
      game: prop.game,
      startISO,
      hitRatePct,
      overHitRatePct: over ?? calculatedOver,
      underHitRatePct: under ?? calculatedUnder,
      overHits: prop.hitRate?.over?.hits ?? overHits,
      overSample: prop.hitRate?.over?.total ?? overValues.length,
      underHits: prop.hitRate?.under?.hits ?? underHits,
      underSample: prop.hitRate?.under?.total ?? overValues.length,
      historyValues: values,
      historyDates: prop.dates ?? [],
    }];
  });
}

export const usePropsStore = create<PropsState>((set, get) => ({
  allProps: [],
  nbaProps: [],
  nhlProps: [],
  isLoading: false,
  error: null,
  lastFetched: null,

  setProps: (props) => set({ allProps: props, lastFetched: Date.now() }),
  setNbaProps: (props) => set({ nbaProps: props }),
  setNhlProps: (props) => set({ nhlProps: props }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  fetchAll: async () => {
    const key = cacheKey();
    const current = get();
    if (loadedCacheKey === key && current.lastFetched && Date.now() - current.lastFetched < PROPS_CACHE_TTL) return;
    const cached = readCached(key);
    if (cached) {
      loadedCacheKey = key;
      set({ nbaProps: cached.nba, nhlProps: cached.nhl, allProps: [...cached.nba, ...cached.nhl], lastFetched: cached.fetchedAt, isLoading: false, error: null });
      return;
    }
    if (inFlight && inFlightKey === key) return inFlight;
    inFlightKey = key;
    inFlight = (async () => {
    set({ isLoading: true, error: null });
    try {
      const [nbaRes, nhlRes] = await Promise.all([
        fetch(`${API_BASE}/props?sport=NBA`, { headers: authHeaders() }),
        fetch(`${API_BASE}/props?sport=NHL`, { headers: authHeaders() }),
      ]);
      if (!nbaRes.ok || !nhlRes.ok) throw new Error('Falha ao buscar props');
      const nba = normalizeProps(await nbaRes.json(), Sport.NBA);
      const nhl = normalizeProps(await nhlRes.json(), Sport.NHL);
      set({
        nbaProps: nba,
        nhlProps: nhl,
        allProps: [...nba, ...nhl],
        lastFetched: Date.now(),
        isLoading: false,
      });
      loadedCacheKey = key;
      try { localStorage.setItem(key, JSON.stringify({ nba, nhl, fetchedAt: Date.now() })); } catch { /* Storage quota/private mode: keep this session's in-memory props. */ }
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false });
    }
    })();
    try { await inFlight; } finally { if (inFlightKey === key) { inFlight = null; inFlightKey = null; } }
  },

  fetchBySport: async (_sport: Sport) => get().fetchAll(),

  getPropsByGame: (game) => get().allProps.filter(p => p.game === game),

  getBestByGame: (game, limit = 4) =>
    get().allProps
      .filter(p => p.game === game && typeof p.hitRatePct === 'number')
      .sort((a, b) => (b.hitRatePct ?? 0) - (a.hitRatePct ?? 0))
      .slice(0, limit),

  getAllGames: () => Array.from(new Set(get().allProps.map(p => p.game))).sort(),
}));
